import type { PublicGamesResponse } from '../api/public-games-types.js';
import { apiBaseUrl } from '../platform/index.js';
import { getAuth, onAuthChange } from './auth.js';
import { supabaseBrowser } from './supabaseClient.js';

/**
 * Open-games client store — caches the public-lobbies browser
 * response so the Inbox LOBBY tab doesn't refetch every mount during
 * a quick back-and-forth. Mirrors friends.ts shape (unloaded /
 * loading / loaded / error) plus a `fetchedAt` timestamp for TTL
 * freshness checks.
 *
 * Why a store at all: a returning user who taps Lobby, opens a
 * game preview, then backs out should see the cached list instantly
 * instead of staring at a spinner. The 30s TTL keeps the data
 * "fresh enough" without a realtime subscription — public lobbies
 * don't change THAT fast.
 *
 * Lifecycle:
 *   - main.ts calls initOpenGames() at boot (after initAuth).
 *   - Auth listener clears the cache on sign-out so the next
 *     sign-in starts fresh.
 *   - InboxScene's LOBBY tab calls refreshOpenGamesIfStale() on
 *     mount; tab re-renders subscribe via onOpenGamesChange.
 *   - No realtime subscription — manual refresh is the contract.
 */

export type OpenGamesState =
  | { readonly kind: 'unloaded' }
  | { readonly kind: 'loading' }
  | {
      readonly kind: 'loaded';
      readonly list: PublicGamesResponse;
      /** epoch ms — drives TTL freshness checks. */
      readonly fetchedAt: number;
    }
  | { readonly kind: 'error'; readonly error: string };

const UNLOADED: OpenGamesState = { kind: 'unloaded' };

/** How long a 'loaded' result counts as fresh before
 *  refreshOpenGamesIfStale() will trigger a re-fetch. 30s feels
 *  right for lobby-list data: long enough to absorb fast back-and-
 *  forth navigation, short enough that revisits after a real pause
 *  still pull current data. */
const TTL_MS = 30_000;

let cachedState: OpenGamesState = UNLOADED;
let initialized = false;
const listeners = new Set<(s: OpenGamesState) => void>();

function setState(next: OpenGamesState): void {
  cachedState = next;
  for (const fn of listeners) {
    try {
      fn(cachedState);
    } catch (e) {
      console.error('openGames listener threw', e);
    }
  }
}

export function initOpenGames(): void {
  if (initialized) return;
  initialized = true;

  // Sign-out clears the cache so the next account starts clean.
  // Unlike friends, we DON'T auto-fetch on sign-in — the browser
  // is tab-bounded, not always-on. The Inbox LOBBY tab triggers
  // the initial fetch when it mounts.
  onAuthChange((auth) => {
    if (!auth.user) {
      setState(UNLOADED);
    }
  });
}

export function getOpenGames(): OpenGamesState {
  return cachedState;
}

export function onOpenGamesChange(
  fn: (s: OpenGamesState) => void
): () => void {
  listeners.add(fn);
  return () => {
    listeners.delete(fn);
  };
}

/** True iff the cache is in 'loaded' state and the data is
 *  younger than TTL_MS. Used by InboxScene's LOBBY-tab mount to
 *  decide whether to fire an initial fetch. */
export function isOpenGamesFresh(): boolean {
  if (cachedState.kind !== 'loaded') return false;
  return Date.now() - cachedState.fetchedAt < TTL_MS;
}

/** Force a re-fetch regardless of cache freshness. Used by the
 *  scene's refresh button. */
export function refreshOpenGames(): void {
  void fetchOpenGames();
}

/** Fetch only if the cache is stale (or never loaded). Used on
 *  scene mount so a quick back-and-forth doesn't re-hit the
 *  network unnecessarily. */
export function refreshOpenGamesIfStale(): void {
  if (isOpenGamesFresh()) return;
  void fetchOpenGames();
}

// ============================================================
// Network ops
// ============================================================

async function authHeader(): Promise<HeadersInit> {
  const supabase = supabaseBrowser();
  const { data } = await supabase.auth.getSession();
  const token = data.session?.access_token;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchOpenGames(): Promise<void> {
  // Guard: don't even try if there's no signed-in user — the
  // endpoint will 401 and we'd just churn cache state. The scene
  // shouldn't be reachable when signed out, but defense in depth.
  if (!getAuth().user) {
    setState(UNLOADED);
    return;
  }

  setState({ kind: 'loading' });
  try {
    const res = await fetch(`${apiBaseUrl()}/api/games/public`, {
      headers: await authHeader(),
    });
    if (!res.ok) {
      const msg = await res.text().catch(() => res.statusText);
      setState({ kind: 'error', error: `${res.status}: ${msg}` });
      return;
    }
    const body = (await res.json()) as PublicGamesResponse;
    setState({ kind: 'loaded', list: body, fetchedAt: Date.now() });
  } catch (err: unknown) {
    setState({
      kind: 'error',
      error: err instanceof Error ? err.message : String(err),
    });
  }
}
